import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  FileText, 
  Download, 
  Upload, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  ChevronLeft, 
  ChevronRight,
  FileDown,
  Loader2,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";

/** Robust extractor for API error messages (handles string, FastAPI validation arrays, and unknowns). */
function formatApiError(error, fallback = "Erro inesperado") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d?.msg ? `${(d.loc || []).slice(-1).join(".")}: ${d.msg}` : JSON.stringify(d)))
      .join(" | ");
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return error?.message || fallback;
}

export default function PowerPlantsBilling({ user, onLogout }) {
  const [reports, setReports] = useState([]);
  const [powerPlants, setPowerPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPowerPlant, setSelectedPowerPlant] = useState("");
  const [referenceMonthFilter, setReferenceMonthFilter] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [billingMonthFilter, setBillingMonthFilter] = useState("");
  const MEASUREMENT_STATUSES = ["Pendente", "Aguardando Envio", "Enviada", "Paga"];
  const statusChip = (st) => ({
    "Pendente": "bg-gray-100 text-gray-700 border-gray-300",
    "Aguardando Envio": "bg-amber-50 text-amber-800 border-amber-300",
    "Enviada": "bg-blue-50 text-blue-800 border-blue-300",
    "Paga": "bg-emerald-50 text-emerald-800 border-emerald-300",
  }[st] || "bg-gray-100 text-gray-700 border-gray-300");
  
  // Form state for adding/editing
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [loadingPreviousBalance, setLoadingPreviousBalance] = useState(false);
  const [formData, setFormData] = useState({
    powerplant_id: "",
    reference_month: "",
    generated_energy_kwh: "",
    consumption_kwh: "",
    generator_injected_kwh: "",
    previous_generator_accumulated_kwh: "0",
    distributor_invoice_amount: "",
    due_date: "",
    payment_date: "",
    measurement_status: "Pendente",
    billing_month: "",
    previous_negative_balance: "0",
    paid_amount: "",
    reading_date: "",
    compensated_energy_kwh: "",
    accumulated_balance_kwh: "",
    previous_accumulated_balance_kwh: "",
    average_energy_kwh: "",
    installed_power_kw: "",
    tariff_value: "",
    fio_b_value: "",
    defaulted_amount: "",
    defaulted_recovery_amount: "",
    last_generation_reading_kwh: "",
    last_reading_month: "",
    notes: "",
    financials_manual: false,
    gross_value: "",
    fio_b_retention: "",
    measurement_value: "",
  });

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedPowerPlant) params.append("powerplant_id", selectedPowerPlant);
      if (referenceMonthFilter) params.append("reference_month", referenceMonthFilter);
      if (statusFilter) params.append("measurement_status", statusFilter);
      if (billingMonthFilter) params.append("billing_month", billingMonthFilter);
      
      const response = await axiosInstance.get(`/powerplant-reports?${params.toString()}`);
      setReports(response.data);
    } catch (error) {
      toast.error("Erro ao carregar relatórios");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedPowerPlant, referenceMonthFilter, statusFilter, billingMonthFilter]);

  const fetchPowerPlants = async () => {
    try {
      const response = await axiosInstance.get("/powerplants");
      setPowerPlants(response.data);
    } catch (error) {
      console.error("Erro ao carregar usinas:", error);
    }
  };

  useEffect(() => {
    fetchPowerPlants();
    fetchReports();
  }, [fetchReports]);

  // Fetch previous balance and installed power when powerplant or month changes
  const fetchPreviousBalanceAndPower = useCallback(async (powerplantId, referenceMonth) => {
    if (!powerplantId || !referenceMonth || referenceMonth.length < 8) return;
    
    try {
      setLoadingPreviousBalance(true);
      const response = await axiosInstance.get(
        `/powerplant-reports/previous-balance/${powerplantId}?reference_month=${encodeURIComponent(referenceMonth)}`
      );
      
      const previousBalance = response.data.previous_accumulated_balance_kwh?.toString().replace(".", ",") || "0";
      const installedPower = response.data.installed_power_kw?.toString().replace(".", ",") || "0";
      const tariffValue = response.data.tariff_value?.toString().replace(".", ",") || "0";
      const fioBValue = response.data.fio_b_value?.toString().replace(".", ",") || "0";
      const averageEnergy = response.data.average_energy_kwh?.toString().replace(".", ",") || "0";
      const lastReading = response.data.last_generation_reading_kwh?.toString().replace(".", ",") || "0";
      const lastReadingMonth = response.data.last_reading_month || "";
      const previousNegative = (response.data.previous_negative_balance ?? 0).toString().replace(".", ",");
      const suggestedBilling = response.data.billing_month || "";
      
      setFormData(prev => {
        const newData = {
          ...prev,
          previous_accumulated_balance_kwh: previousBalance,
          previous_negative_balance: previousNegative,
          previous_generator_accumulated_kwh: (response.data.previous_generator_accumulated_kwh ?? 0).toString().replace(".", ","),
          billing_month: prev.billing_month || suggestedBilling,
          installed_power_kw: installedPower,
          tariff_value: tariffValue,
          fio_b_value: fioBValue,
          average_energy_kwh: averageEnergy,
          last_generation_reading_kwh: lastReading,
          last_reading_month: lastReadingMonth,
        };
        
        // Recalculate accumulated balance with new previous balance
        const parseVal = (v) => !v ? 0 : parseFloat(v.toString().replace(",", ".")) || 0;
        const generatedEnergy = parseVal(newData.generated_energy_kwh);
        const consumption = parseVal(newData.consumption_kwh);
        const compensatedEnergy = parseVal(newData.compensated_energy_kwh);
        const prevBal = parseVal(previousBalance);
        const result = generatedEnergy - consumption - parseVal(newData.generator_injected_kwh) - compensatedEnergy + prevBal;
        newData.accumulated_balance_kwh = result.toFixed(2).replace(".", ",");
        
        return newData;
      });
      
      if (response.data.previous_month) {
        toast.info(`Dados carregados do cadastro da usina`);
      }
    } catch (error) {
      console.error("Erro ao buscar dados da usina:", error);
    } finally {
      setLoadingPreviousBalance(false);
    }
  }, []);

  // Effect to fetch previous balance when form fields change
  useEffect(() => {
    if (showForm && !editingReport && formData.powerplant_id && formData.reference_month) {
      const timeoutId = setTimeout(() => {
        fetchPreviousBalanceAndPower(formData.powerplant_id, formData.reference_month);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [showForm, editingReport, formData.powerplant_id, formData.reference_month, fetchPreviousBalanceAndPower]);

  // Helper to parse form values to numbers
  const parseFormValue = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(",", ".")) || 0;
  };

  // Calculate accumulated balance: Energia Gerada - Consumo - Energia Compensada + Saldo Anterior
  const calculateAccumulatedBalance = (data) => {
    const generatedEnergy = parseFormValue(data.generated_energy_kwh);
    const consumption = parseFormValue(data.consumption_kwh);
    const compensatedEnergy = parseFormValue(data.compensated_energy_kwh);
    const previousBalance = parseFormValue(data.previous_accumulated_balance_kwh);
    const generatorInjected = parseFormValue(data.generator_injected_kwh);
    
    const result = generatedEnergy - consumption - generatorInjected - compensatedEnergy + previousBalance;
    return result.toFixed(2).replace(".", ",");
  };

  // Handle form field changes with auto-calculation
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate accumulated balance when relevant fields change
      if (['generated_energy_kwh', 'consumption_kwh', 'generator_injected_kwh', 'compensated_energy_kwh', 'previous_accumulated_balance_kwh'].includes(field)) {
        newData.accumulated_balance_kwh = calculateAccumulatedBalance(newData);
      }
      
      return newData;
    });
  };

  const resetForm = () => {
    setFormData({
      powerplant_id: "",
      reference_month: "",
      generated_energy_kwh: "",
      consumption_kwh: "",
      generator_injected_kwh: "",
      previous_generator_accumulated_kwh: "0",
      distributor_invoice_amount: "",
      due_date: "",
      payment_date: "",
      measurement_status: "Pendente",
      billing_month: "",
      previous_negative_balance: "0",
      paid_amount: "",
      reading_date: "",
      compensated_energy_kwh: "",
      accumulated_balance_kwh: "",
      previous_accumulated_balance_kwh: "",
      average_energy_kwh: "",
      installed_power_kw: "",
      tariff_value: "",
      fio_b_value: "",
      defaulted_amount: "",
      defaulted_recovery_amount: "",
      last_generation_reading_kwh: "",
      last_reading_month: "",
      notes: "",
      financials_manual: false,
      gross_value: "",
      fio_b_retention: "",
      measurement_value: "",
    });
    setEditingReport(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.powerplant_id || !formData.reference_month) {
      toast.error("Usina e Mês de Referência são obrigatórios");
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        ...formData,
        generated_energy_kwh: parseFloat(formData.generated_energy_kwh?.toString().replace(",", ".")) || 0,
        consumption_kwh: parseFloat(formData.consumption_kwh?.toString().replace(",", ".")) || 0,
        generator_injected_kwh: parseFloat(formData.generator_injected_kwh?.toString().replace(",", ".")) || 0,
        distributor_invoice_amount: parseFloat(formData.distributor_invoice_amount?.toString().replace(",", ".")) || 0,
        compensated_energy_kwh: parseFloat(formData.compensated_energy_kwh?.toString().replace(",", ".")) || 0,
        accumulated_balance_kwh: parseFloat(formData.accumulated_balance_kwh?.toString().replace(",", ".")) || 0,
        previous_accumulated_balance_kwh: parseFloat(formData.previous_accumulated_balance_kwh?.toString().replace(",", ".")) || 0,
        average_energy_kwh: parseFloat(formData.average_energy_kwh?.toString().replace(",", ".")) || 0,
        installed_power_kw: parseFloat(formData.installed_power_kw?.toString().replace(",", ".")) || 0,
        tariff_value: parseFloat(formData.tariff_value?.toString().replace(",", ".")) || 0,
        fio_b_value: parseFloat(formData.fio_b_value?.toString().replace(",", ".")) || 0,
        defaulted_amount: parseFloat(formData.defaulted_amount?.toString().replace(",", ".")) || 0,
        defaulted_recovery_amount: parseFloat(formData.defaulted_recovery_amount?.toString().replace(",", ".")) || 0,
        // Optional float — send null when empty (backend expects Optional[float])
        last_generation_reading_kwh: (formData.last_generation_reading_kwh || "").toString().trim()
          ? parseFloat(formData.last_generation_reading_kwh.toString().replace(",", "."))
          : null,
        // Optional strings — send null when empty
        last_reading_month: (formData.last_reading_month || "").toString().trim() || null,
        due_date: (formData.due_date || "").toString().trim() || null,
        payment_date: (formData.payment_date || "").toString().trim() || null,
        measurement_status: formData.measurement_status || "Pendente",
        billing_month: (formData.billing_month || "").toString().trim().toUpperCase() || null,
        paid_amount: (formData.paid_amount || "").toString().trim()
          ? parseFloat(formData.paid_amount.toString().replace(",", "."))
          : null,
        reading_date: (formData.reading_date || "").toString().trim() || null,
        notes: (formData.notes || "").toString().trim() || null,
        financials_manual: !!formData.financials_manual,
      };
      delete payload.previous_negative_balance;
      delete payload.previous_generator_accumulated_kwh;
      const toNum = (v) => parseFloat((v || "").toString().replace(",", "."));
      if (formData.financials_manual) {
        ["gross_value", "fio_b_retention", "measurement_value"].forEach((k) => {
          const n = toNum(formData[k]);
          if (!Number.isNaN(n)) payload[k] = n; else delete payload[k];
        });
      } else {
        delete payload.gross_value; delete payload.fio_b_retention; delete payload.measurement_value;
      }

      if (editingReport) {
        await axiosInstance.put(`/powerplant-reports/${editingReport.id}`, payload);
        toast.success("Relatório atualizado com sucesso");
      } else {
        await axiosInstance.post("/powerplant-reports", payload);
        toast.success("Relatório criado com sucesso");
      }
      
      resetForm();
      fetchReports();
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao salvar relatório"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Nome de arquivo seguro: "CGH Jabuti" → "CGH_Jabuti"
  const safeFileName = (txt) =>
    (txt || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "usina";

  const handleDownloadSinglePdf = async (report) => {
    try {
      const res = await axiosInstance.get(`/powerplant-reports/${report.id}/single-pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const safeMonth = (report.reference_month || "medicao").replace(/\//g, "-");
      link.setAttribute("download", `Medicao_${safeFileName(report.powerplant_name)}_${safeMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF da medição gerado com sucesso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PDF da medição");
    }
  };

  const handleDownloadDocxTemplate = async () => {
    try {
      const res = await axiosInstance.get(`/powerplant-reports/template-docx`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_relatorio_medicao.docx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Modelo editável baixado com sucesso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao baixar modelo");
    }
  };

  const handleDownloadSingleDocx = async (report) => {
    try {
      const res = await axiosInstance.get(`/powerplant-reports/${report.id}/single-docx`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const safeMonth = (report.reference_month || "medicao").replace(/\//g, "-");
      link.setAttribute("download", `Medicao_${safeFileName(report.powerplant_name)}_${safeMonth}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Word da medição gerado com sucesso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar Word da medição");
    }
  };

  const handleEdit = (report) => {
    setFormData({
      powerplant_id: report.powerplant_id || "",
      reference_month: report.reference_month || "",
      generated_energy_kwh: report.generated_energy_kwh?.toString().replace(".", ",") || "",
      consumption_kwh: report.consumption_kwh?.toString().replace(".", ",") || "",
      generator_injected_kwh: report.generator_injected_kwh?.toString().replace(".", ",") || "",
      previous_generator_accumulated_kwh: (report.previous_generator_accumulated_kwh ?? 0).toString().replace(".", ","),
      distributor_invoice_amount: report.distributor_invoice_amount?.toString().replace(".", ",") || "",
      due_date: report.due_date || "",
      payment_date: report.payment_date || "",
      measurement_status: report.measurement_status || "Pendente",
      billing_month: report.billing_month || "",
      previous_negative_balance: (report.previous_negative_balance ?? 0).toString().replace(".", ","),
      paid_amount: report.paid_amount != null ? report.paid_amount.toString().replace(".", ",") : "",
      reading_date: report.reading_date || "",
      compensated_energy_kwh: report.compensated_energy_kwh?.toString().replace(".", ",") || "",
      accumulated_balance_kwh: report.accumulated_balance_kwh?.toString().replace(".", ",") || "",
      previous_accumulated_balance_kwh: report.previous_accumulated_balance_kwh?.toString().replace(".", ",") || "0",
      average_energy_kwh: report.average_energy_kwh?.toString().replace(".", ",") || "",
      installed_power_kw: report.installed_power_kw?.toString().replace(".", ",") || "0",
      tariff_value: report.tariff_value?.toString().replace(".", ",") || "0",
      fio_b_value: report.fio_b_value?.toString().replace(".", ",") || "0",
      defaulted_amount: report.defaulted_amount?.toString().replace(".", ",") || "",
      defaulted_recovery_amount: report.defaulted_recovery_amount?.toString().replace(".", ",") || "",
      notes: report.notes || "",
      financials_manual: !!report.financials_manual,
      gross_value: report.gross_value != null ? report.gross_value.toString().replace(".", ",") : "",
      fio_b_retention: report.fio_b_retention != null ? report.fio_b_retention.toString().replace(".", ",") : "",
      measurement_value: (report.measurement_base_value ?? report.measurement_value) != null ? (report.measurement_base_value ?? report.measurement_value).toString().replace(".", ",") : "",
    });
    setEditingReport(report);
    setShowForm(true);
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm("Tem certeza que deseja excluir este relatório?")) return;
    
    try {
      await axiosInstance.delete(`/powerplant-reports/${reportId}`);
      toast.success("Relatório excluído com sucesso");
      fetchReports();
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao excluir relatório"));
    }
  };

  const handleStatusChange = async (report, status) => {
    try {
      await axiosInstance.put(`/powerplant-reports/${report.id}`, { measurement_status: status });
      setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, measurement_status: status } : r)));
      toast.success(`Status Medição: ${status}`);
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao atualizar status"));
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelectPage = (ids) =>
    setSelectedIds((prev) => (ids.every((id) => prev.includes(id)) ? prev.filter((x) => !ids.includes(x)) : Array.from(new Set([...prev, ...ids]))));

  const handleBulkStatus = async (status) => {
    if (!selectedIds.length || !status) return;
    try {
      const r = await axiosInstance.post("/powerplant-reports/bulk-status", { ids: selectedIds, measurement_status: status });
      toast.success(r.data?.message || "Status atualizado");
      setReports((prev) => prev.map((x) => (selectedIds.includes(x.id) ? { ...x, measurement_status: status } : x)));
      setSelectedIds([]);
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao alterar status em massa"));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Excluir ${selectedIds.length} relatório(s) selecionado(s)? Esta ação não pode ser desfeita.`)) return;
    try {
      const r = await axiosInstance.post("/powerplant-reports/bulk-delete", { ids: selectedIds });
      toast.success(r.data?.message || "Relatórios excluídos");
      setSelectedIds([]);
      fetchReports();
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao excluir relatórios"));
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post("/powerplant-reports/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados. Verifique o console.`);
        console.log("Erros de importação:", response.data.errors);
      }
      
      fetchReports();
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao importar arquivo"));
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedPowerPlant) params.append("powerplant_id", selectedPowerPlant);
      
      const response = await axiosInstance.get(`/powerplant-reports/export-csv?${params.toString()}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "relatorios_usinas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Arquivo exportado com sucesso");
    } catch (error) {
      toast.error("Erro ao exportar arquivo");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/powerplant-reports/template", {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_relatorios_usinas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Modelo baixado com sucesso");
    } catch (error) {
      toast.error("Erro ao baixar modelo");
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedPowerPlant) {
      toast.error("Selecione uma usina para gerar o relatório PDF");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(`/powerplant-reports/pdf/${selectedPowerPlant}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement("a");
      link.href = url;
      const ppName = powerPlants.find((p) => p.id === selectedPowerPlant)?.plant_name;
      link.setAttribute("download", `Relatorio_Usina_${safeFileName(ppName)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Relatório PDF gerado com sucesso");
    } catch (error) {
      toast.error(formatApiError(error, "Erro ao gerar relatório PDF"));
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(reports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReports = reports.slice(startIndex, startIndex + itemsPerPage);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0,00";
    return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "R$ 0,00";
    return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  // Access control
  const isAdmin = user?.role === "admin";
  const isOperador = user?.role === "operador";
  const isUsineiro = user?.role === "usineiro";
  const isUsina = user?.role === "usina";
  const canEdit = isAdmin || isOperador;
  const canView = canEdit || isUsina || isUsineiro;

  if (!canView) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600">Acesso restrito.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Relatório de Usinas</h1>
              <p className="text-gray-600">Gerencie os dados de geração e consumo das usinas</p>
            </div>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadDocxTemplate}
                data-testid="download-docx-template-btn"
                title="Baixar modelo editável (.docx) com placeholders para redesenhar o layout"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Baixar Modelo
              </Button>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-report-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Registro
              </Button>
            </div>
          )}
        </div>

        {/* Import/Export Actions */}
        {canEdit && (
          <Card className="border-0 shadow-md">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    className="hidden"
                    data-testid="import-csv-input"
                  />
                  <Button variant="outline" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar CSV
                    </span>
                  </Button>
                </label>
                
                <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                
                <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                  <FileDown className="w-4 h-4 mr-2" />
                  Baixar Modelo
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={handleDownloadPDF} 
                  disabled={!selectedPowerPlant}
                  className={selectedPowerPlant ? "bg-blue-50 hover:bg-blue-100 border-blue-300" : ""}
                  data-testid="download-pdf-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Gerar Relatório PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg">Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usina</label>
                <select
                  value={selectedPowerPlant}
                  onChange={(e) => {
                    setSelectedPowerPlant(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="filter-powerplant"
                >
                  <option value="">Todas as Usinas</option>
                  {powerPlants.map((pp) => (
                    <option key={pp.id} value={pp.id}>
                      {pp.name || pp.plant_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mês de Referência</label>
                <Input
                  value={referenceMonthFilter}
                  onChange={(e) => {
                    setReferenceMonthFilter(e.target.value.toUpperCase());
                    setCurrentPage(1);
                  }}
                  placeholder="Ex: JAN/2025"
                  data-testid="filter-month"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mês de Faturamento</label>
                <Input
                  value={billingMonthFilter}
                  onChange={(e) => { setBillingMonthFilter(e.target.value.toUpperCase()); setCurrentPage(1); }}
                  placeholder="Ex: FEV/2025"
                  data-testid="filter-billing-month"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status Medição</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="filter-measurement-status"
                >
                  <option value="">Todos os status</option>
                  {MEASUREMENT_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Itens por página</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="items-per-page"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="border-0 shadow-lg border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingReport ? "Editar Registro" : "Novo Registro"}</span>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Usina */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usina <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.powerplant_id}
                      onChange={(e) => setFormData({ ...formData, powerplant_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                      data-testid="form-powerplant"
                    >
                      <option value="">Selecione uma usina</option>
                      {powerPlants.map((pp) => (
                        <option key={pp.id} value={pp.id}>
                          {pp.name || pp.plant_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Mês de Referência */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mês de Referência <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.reference_month}
                      onChange={(e) => setFormData({ ...formData, reference_month: e.target.value.toUpperCase() })}
                      placeholder="Ex: JAN/2025"
                      required
                      data-testid="form-reference-month"
                    />
                  </div>

                  {/* Mês de Faturamento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mês de Faturamento</label>
                    <Input
                      value={formData.billing_month}
                      onChange={(e) => setFormData({ ...formData, billing_month: e.target.value.toUpperCase() })}
                      placeholder="Ex: FEV/2025"
                      data-testid="form-billing-month"
                    />
                    <p className="text-xs text-gray-500 mt-1">Mês de envio para pagamento (padrão: mês seguinte)</p>
                  </div>
                  
                  {/* Potência Instalada (Read-only from powerplant) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Potência Instalada (kW)
                      {loadingPreviousBalance && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
                    </label>
                    <Input
                      type="text"
                      value={formData.installed_power_kw}
                      readOnly
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Carregado do cadastro"
                      data-testid="form-installed-power"
                    />
                    <p className="text-xs text-gray-500 mt-1">Do cadastro da usina</p>
                  </div>
                  
                  {/* Tarifa (pré-carregada do cadastro, editável) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tarifa (R$)
                      {loadingPreviousBalance && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
                    </label>
                    <Input
                      type="text"
                      value={formData.tariff_value}
                      onChange={(e) => setFormData({ ...formData, tariff_value: e.target.value })}
                      placeholder="Ex: 0,89"
                      data-testid="form-tariff"
                    />
                    <p className="text-xs text-gray-500 mt-1">{editingReport ? "Valor registrado nesta medição (editável)" : "Pré-carregado do cadastro da usina — pode ser ajustado para esta medição"}</p>
                  </div>
                  
                  {/* Fio B (pré-carregado do cadastro, editável) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fio B (R$)
                      {loadingPreviousBalance && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
                    </label>
                    <Input
                      type="text"
                      value={formData.fio_b_value}
                      onChange={(e) => setFormData({ ...formData, fio_b_value: e.target.value })}
                      placeholder="Ex: 0,25"
                      data-testid="form-fio-b"
                    />
                    <p className="text-xs text-gray-500 mt-1">{editingReport ? "Valor registrado nesta medição (editável)" : "Pré-carregado do cadastro da usina — pode ser ajustado para esta medição"}</p>
                  </div>
                  
                  {/* Energia Média (Read-only from powerplant) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energia Média (kWh)
                      {loadingPreviousBalance && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
                    </label>
                    <Input
                      type="text"
                      value={formData.average_energy_kwh}
                      readOnly
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Carregado do cadastro"
                      data-testid="form-average-energy"
                    />
                    <p className="text-xs text-gray-500 mt-1">Do cadastro da usina</p>
                  </div>

                  {/* Última Leitura de Geração */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Última Leitura de Geração (kWh)
                    </label>
                    <Input
                      type="text"
                      value={formData.last_generation_reading_kwh || ""}
                      onChange={(e) => handleFormChange('last_generation_reading_kwh', e.target.value)}
                      placeholder="Ex: 12000"
                      data-testid="form-last-generation-reading"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pré-preenchido do último registro; edite se necessário</p>
                  </div>

                  {/* Mês de Referência da Última Leitura */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mês de Referência (Última)
                    </label>
                    <Input
                      type="text"
                      value={formData.last_reading_month || ""}
                      onChange={(e) => setFormData({ ...formData, last_reading_month: e.target.value.toUpperCase() })}
                      placeholder="Ex: JAN/2025"
                      data-testid="form-last-reading-month"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pré-preenchido do último registro; edite se necessário</p>
                  </div>

                  {/* Energia Gerada */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energia Gerada (kWh)
                    </label>
                    <Input
                      type="text"
                      value={formData.generated_energy_kwh}
                      onChange={(e) => handleFormChange('generated_energy_kwh', e.target.value)}
                      placeholder="Ex: 15000"
                      data-testid="form-generated-energy"
                    />
                  </div>
                  
                  {/* Consumo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Consumo (kWh)
                    </label>
                    <Input
                      type="text"
                      value={formData.consumption_kwh}
                      onChange={(e) => handleFormChange('consumption_kwh', e.target.value)}
                      placeholder="Ex: 12000"
                      data-testid="form-consumption"
                    />
                  </div>
                  
                  {/* Fatura da Distribuidora */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fatura da Distribuidora (R$)
                    </label>
                    <Input
                      type="text"
                      value={formData.distributor_invoice_amount}
                      onChange={(e) => setFormData({ ...formData, distributor_invoice_amount: e.target.value })}
                      placeholder="Ex: 1500,00"
                      data-testid="form-distributor-invoice"
                    />
                  </div>
                  
                  {/* Data de Vencimento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Vencimento
                    </label>
                    <Input
                      type="text"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      placeholder="Ex: 15/01/2025"
                      data-testid="form-due-date"
                    />
                  </div>
                  
                  {/* Data de Pagamento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Pagamento
                    </label>
                    <Input
                      type="text"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      placeholder="Ex: 20/01/2025"
                      data-testid="form-payment-date"
                    />
                    <p className="text-xs text-gray-500 mt-1">Data para pagamento da medição (consta no relatório)</p>
                  </div>

                  {/* Status Medição */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status Medição</label>
                    <select
                      value={formData.measurement_status || "Pendente"}
                      onChange={(e) => setFormData({ ...formData, measurement_status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md h-10"
                      data-testid="form-measurement-status"
                    >
                      {MEASUREMENT_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Ao criar, inicia como Pendente</p>
                  </div>

                  {/* Valor Pago */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Pago (R$)
                    </label>
                    <Input
                      type="text"
                      value={formData.paid_amount}
                      onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                      placeholder="Ex: 6670,50"
                      data-testid="form-paid-amount"
                    />
                    <p className="text-xs text-gray-500 mt-1">Valor efetivamente pago desta medição</p>
                  </div>
                  
                  {/* Data de Leitura */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Leitura
                    </label>
                    <Input
                      type="text"
                      value={formData.reading_date}
                      onChange={(e) => setFormData({ ...formData, reading_date: e.target.value })}
                      placeholder="Ex: 10/01/2025"
                      data-testid="form-reading-date"
                    />
                  </div>
                  
                  {/* Energia Compensada */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energia Compensada (kWh)
                    </label>
                    <Input
                      type="text"
                      value={formData.compensated_energy_kwh}
                      onChange={(e) => handleFormChange('compensated_energy_kwh', e.target.value)}
                      placeholder="Ex: 10000"
                      data-testid="form-compensated-energy"
                    />
                  </div>
                  
                  {/* Saldo Anterior (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Saldo Anterior (kWh)
                      {loadingPreviousBalance && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
                    </label>
                    <Input
                      type="text"
                      value={formData.previous_accumulated_balance_kwh}
                      readOnly
                      className="bg-gray-100 cursor-not-allowed"
                      placeholder="Carregado do mês anterior"
                      data-testid="form-previous-balance"
                    />
                    <p className="text-xs text-gray-500 mt-1">Valor automático do mês anterior</p>
                  </div>
                  
                  {/* Saldo Acumulado (Auto-calculated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Saldo Acumulado (kWh)
                    </label>
                    <Input
                      type="text"
                      value={formData.accumulated_balance_kwh}
                      readOnly
                      className="bg-blue-50 font-semibold"
                      placeholder="Calculado automaticamente"
                      data-testid="form-accumulated-balance"
                    />
                    <p className="text-xs text-blue-600 mt-1">= E. Injetada - E. Compensada + Saldo Anterior</p>
                  </div>

                  {/* Injetado Geradora */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Injetado Geradora (kWh)</label>
                    <Input
                      type="text"
                      value={formData.generator_injected_kwh}
                      onChange={(e) => handleFormChange('generator_injected_kwh', e.target.value)}
                      placeholder="Ex: 500"
                      data-testid="form-generator-injected"
                    />
                    <p className="text-xs text-gray-500 mt-1">Energia que fica na UC geradora (descontada da Injetada)</p>
                  </div>

                  {/* Acumulado na Geradora (Auto-calculated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Acumulado na Geradora (kWh)</label>
                    <Input
                      type="text"
                      value={(() => {
                        const p = parseFloat((formData.previous_generator_accumulated_kwh || '0').toString().replace(',', '.')) || 0;
                        const g = parseFloat((formData.generator_injected_kwh || '0').toString().replace(',', '.')) || 0;
                        return (p + g).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
                      readOnly
                      className="bg-amber-50 font-semibold"
                      data-testid="form-generator-accumulated"
                    />
                    <p className="text-xs text-amber-700 mt-1">= Acumulado anterior ({formData.previous_generator_accumulated_kwh || '0'}) + Injetado Geradora</p>
                  </div>

                  {/* Energia Injetada (Auto-calculated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energia Injetada (kWh)
                    </label>
                    <Input
                      type="text"
                      value={(() => {
                        const g = parseFloat((formData.generated_energy_kwh || '0').toString().replace(',', '.')) || 0;
                        const c = parseFloat((formData.consumption_kwh || '0').toString().replace(',', '.')) || 0;
                        const gi = parseFloat((formData.generator_injected_kwh || '0').toString().replace(',', '.')) || 0;
                        return (g - c - gi).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
                      readOnly
                      className="bg-emerald-50 font-semibold"
                      placeholder="Calculado automaticamente"
                      data-testid="form-injected-energy"
                    />
                    <p className="text-xs text-emerald-600 mt-1">= Energia Gerada - Consumo - Injetado Geradora</p>
                  </div>

                  {/* Inadimplência R$ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inadimplência (R$)
                    </label>
                    <Input
                      type="text"
                      value={formData.defaulted_amount}
                      onChange={(e) => handleFormChange('defaulted_amount', e.target.value)}
                      placeholder="Ex: 0,00"
                      data-testid="form-defaulted-amount"
                    />
                    <p className="text-xs text-gray-500 mt-1">Valor de faturas em aberto do período</p>
                  </div>

                  {/* Recuperação de Inadimplência R$ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recuperação de Inadimplência (R$)
                    </label>
                    <Input
                      type="text"
                      value={formData.defaulted_recovery_amount}
                      onChange={(e) => handleFormChange('defaulted_recovery_amount', e.target.value)}
                      placeholder="Ex: 0,00"
                      data-testid="form-defaulted-recovery-amount"
                    />
                    <p className="text-xs text-gray-500 mt-1">Valor recuperado de inadimplências anteriores</p>
                  </div>
                </div>

                {/* Valor da Medição (calculated) */}
                {(() => {
                  const parse = (v) => parseFloat((v || '0').toString().replace(',', '.')) || 0;
                  const comp = parse(formData.compensated_energy_kwh);
                  const tariff = parse(formData.tariff_value);
                  const fioB = parse(formData.fio_b_value);
                  const distInv = parse(formData.distributor_invoice_amount);
                  const defaulted = parse(formData.defaulted_amount);
                  const recovery = parse(formData.defaulted_recovery_amount);
                  const carry = Math.min(0, parse(formData.previous_negative_balance));
                  const gross = comp * (tariff - fioB);
                  const value = gross - distInv - defaulted * 0.5 + recovery * 0.5 + carry;
                  const positive = value >= 0;
                  return (
                    <div
                      className={`rounded-lg border-2 p-4 ${positive ? 'bg-emerald-50 border-emerald-500' : 'bg-rose-50 border-rose-500'}`}
                      data-testid="form-measurement-value-box"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <div className={`text-sm font-bold ${positive ? 'text-emerald-800' : 'text-rose-800'}`}>
                            Valor da Medição
                          </div>
                          <div className="text-[11px] text-gray-600 italic mt-0.5">
                            Fórmula: E. Compensada × (Tarifa − Fio B) − Fatura Distribuidora − Inadimplência × 0,5 + Recuperação de Inadimplência × 0,5
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {comp.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} × (R$ {tariff.toLocaleString('pt-BR', { minimumFractionDigits: 4 })} − R$ {fioB.toLocaleString('pt-BR', { minimumFractionDigits: 4 })})
                            {' '}− R$ {distInv.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} − R$ {defaulted.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} × 0,5
                            {' '}+ R$ {recovery.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} × 0,5
                          </div>
                          {carry < 0 && (
                            <div className="text-xs font-semibold text-rose-700 mt-1" data-testid="form-previous-negative-balance">
                              Desconto Faturas Negativas (mês anterior): R$ {carry.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} → Total = R$ {(value - carry).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} − R$ {Math.abs(carry).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                          )}
                          {!positive && (
                            <div className="text-[11px] text-rose-700 mt-1">Saldo negativo será descontado na medição do mês seguinte.</div>
                          )}
                        </div>
                        <div className={`text-2xl font-bold ${positive ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="form-measurement-value">
                          R$ {value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {/* Valores financeiros — manual (CSV/edição) */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="form-financials-manual-box">
                  <label className="flex items-center gap-2 text-sm font-semibold text-amber-900 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.financials_manual}
                      onChange={(e) => {
                        const on = e.target.checked;
                        if (on) {
                          const comp = parseFloat((formData.compensated_energy_kwh || "0").toString().replace(",", ".")) || 0;
                          const tariff = parseFloat((formData.tariff_value || "0").toString().replace(",", ".")) || 0;
                          const fioB = parseFloat((formData.fio_b_value || "0").toString().replace(",", ".")) || 0;
                          const distInv = parseFloat((formData.distributor_invoice_amount || "0").toString().replace(",", ".")) || 0;
                          const defaulted = parseFloat((formData.defaulted_amount || "0").toString().replace(",", ".")) || 0;
                          const recovery = parseFloat((formData.defaulted_recovery_amount || "0").toString().replace(",", ".")) || 0;
                          const fmt = (n) => n.toFixed(2).replace(".", ",");
                          setFormData({
                            ...formData,
                            financials_manual: true,
                            gross_value: formData.gross_value || fmt(comp * tariff),
                            fio_b_retention: formData.fio_b_retention || fmt(comp * fioB),
                            measurement_value: formData.measurement_value || fmt(comp * (tariff - fioB) - distInv - defaulted * 0.5 + recovery * 0.5),
                          });
                        } else {
                          setFormData({ ...formData, financials_manual: false });
                        }
                      }}
                      data-testid="form-financials-manual-toggle"
                    />
                    Informar valores financeiros manualmente (sobrepõe o cálculo)
                  </label>
                  <p className="text-[11px] text-amber-800/80 mt-1">
                    Use quando o CSV já trouxe Valor Bruto / Retenção Fio B / Total da Medição ou quando precisar ajustar manualmente. Desmarque para voltar ao cálculo automático.
                  </p>
                  {formData.financials_manual && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Valor Bruto (R$)</label>
                        <Input type="text" value={formData.gross_value} onChange={(e) => setFormData({ ...formData, gross_value: e.target.value })} data-testid="form-gross-value" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Retenção Fio B (R$)</label>
                        <Input type="text" value={formData.fio_b_retention} onChange={(e) => setFormData({ ...formData, fio_b_retention: e.target.value })} data-testid="form-fio-b-retention" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Valor da Medição (R$) — antes do Desconto Faturas Negativas</label>
                        <Input type="text" value={formData.measurement_value} onChange={(e) => setFormData({ ...formData, measurement_value: e.target.value })} data-testid="form-measurement-value-input" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={2}
                    placeholder="Notas adicionais..."
                    data-testid="form-notes"
                  />
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={loading} data-testid="form-submit-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {editingReport ? "Atualizar" : "Salvar"}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Registros ({reports.length} total)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : paginatedReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum registro encontrado
              </div>
            ) : (
              <>
                {canEdit && selectedIds.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="bulk-actions-bar">
                    <span className="text-sm font-medium text-slate-800">{selectedIds.length} relatório(s) selecionado(s)</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        defaultValue=""
                        onChange={(e) => { handleBulkStatus(e.target.value); e.target.value = ""; }}
                        className="text-sm px-3 py-1.5 border border-gray-300 rounded-md bg-white"
                        data-testid="bulk-status-select"
                        title="Alterar Status Medição dos selecionados"
                      >
                        <option value="" disabled>Alterar status para…</option>
                        {MEASUREMENT_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                      <Button variant="outline" size="sm" onClick={() => setSelectedIds([])} data-testid="bulk-clear-btn">Limpar seleção</Button>
                      {isAdmin && (
                        <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                          <Trash2 className="w-4 h-4 mr-1" /> Excluir selecionados
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="reports-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        {canEdit && (
                          <th className="px-2 py-3 text-center w-8">
                            <input
                              type="checkbox"
                              checked={paginatedReports.length > 0 && paginatedReports.every((r) => selectedIds.includes(r.id))}
                              onChange={() => toggleSelectPage(paginatedReports.map((r) => r.id))}
                              data-testid="select-all-reports"
                              title="Selecionar todos da página"
                            />
                          </th>
                        )}
                        <th className="px-2 py-3 text-left font-medium text-gray-700">Usina</th>
                        <th className="px-2 py-3 text-left font-medium text-gray-700">Mês</th>
                        <th className="px-2 py-3 text-left font-medium text-gray-700">Mês Fat.</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Potência</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Tarifa</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Fio B</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">E. Média</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">E. Gerada</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Consumo</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Inj. Geradora</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Acum. Geradora</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">E. Comp.</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Saldo Ant.</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Saldo Acum.</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Desc. Fat. Neg.</th>
                        <th className="px-2 py-3 text-right font-medium text-gray-700">Total Medição</th>
                        <th className="px-2 py-3 text-center font-medium text-gray-700">Status Medição</th>
                        {canEdit && (
                          <th className="px-2 py-3 text-center font-medium text-gray-700">Ações</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReports.map((report, index) => (
                        <tr 
                          key={report.id} 
                          className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}
                          data-testid={`report-row-${report.id}`}
                        >
                          {canEdit && (
                            <td className="px-2 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(report.id)}
                                onChange={() => toggleSelect(report.id)}
                                data-testid={`select-report-${report.id}`}
                              />
                            </td>
                          )}
                          <td className="px-2 py-3 font-medium">{report.powerplant_name}</td>
                          <td className="px-2 py-3">{report.reference_month}</td>
                          <td className="px-2 py-3" data-testid={`report-billing-month-${report.id}`}>{report.billing_month || '—'}</td>
                          <td className="px-2 py-3 text-right">{formatNumber(report.installed_power_kw)} kW</td>
                          <td className="px-2 py-3 text-right">R$ {formatNumber(report.tariff_value)}</td>
                          <td className="px-2 py-3 text-right">R$ {formatNumber(report.fio_b_value)}</td>
                          <td className="px-2 py-3 text-right">{formatNumber(report.average_energy_kwh)}</td>
                          <td className="px-2 py-3 text-right">{formatNumber(report.generated_energy_kwh)}</td>
                          <td className="px-2 py-3 text-right">{formatNumber(report.consumption_kwh)}</td>
                          <td className="px-2 py-3 text-right" data-testid={`report-generator-injected-${report.id}`}>{formatNumber(report.generator_injected_kwh)}</td>
                          <td className="px-2 py-3 text-right font-semibold text-amber-800" data-testid={`report-generator-accumulated-${report.id}`}>{formatNumber(report.generator_accumulated_kwh)}</td>
                          <td className="px-2 py-3 text-right">{formatNumber(report.compensated_energy_kwh)}</td>
                          <td className="px-2 py-3 text-right text-gray-500">{formatNumber(report.previous_accumulated_balance_kwh)}</td>
                          <td className="px-2 py-3 text-right font-semibold">{formatNumber(report.accumulated_balance_kwh)}</td>
                          <td className={`px-2 py-3 text-right ${(report.previous_negative_balance ?? 0) < 0 ? 'text-rose-700 font-semibold' : 'text-gray-400'}`} data-testid={`report-previous-negative-${report.id}`}>
                            {(report.previous_negative_balance ?? 0) < 0 ? `R$ ${formatNumber(report.previous_negative_balance)}` : '—'}
                          </td>
                          <td className={`px-2 py-3 text-right font-semibold ${(report.measurement_value ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid={`report-measurement-${report.id}`}>
                            {report.measurement_value != null ? `R$ ${formatNumber(report.measurement_value)}` : '—'}
                            {(report.measurement_value ?? 0) < 0 && (
                              <span className="block text-[10px] font-semibold text-rose-700" data-testid={`report-negative-note-${report.id}`}>
                                Negativa — a descontar na próxima medição
                              </span>
                            )}
                            {report.financials_manual && <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" title="Valores financeiros informados manualmente">manual</span>}
                          </td>
                          <td className="px-2 py-3 text-center">
                            {canEdit ? (
                              <select
                                value={report.measurement_status || "Pendente"}
                                onChange={(e) => handleStatusChange(report, e.target.value)}
                                className={`text-xs font-semibold px-2 py-1 rounded border ${statusChip(report.measurement_status || "Pendente")}`}
                                data-testid={`status-select-${report.id}`}
                              >
                                {MEASUREMENT_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                              </select>
                            ) : (
                              <span className={`text-xs font-semibold px-2 py-1 rounded border ${statusChip(report.measurement_status || "Pendente")}`}>{report.measurement_status || "Pendente"}</span>
                            )}
                          </td>
                          {canEdit && (
                            <td className="px-2 py-3">
                              <div className="flex gap-1 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadSinglePdf(report)}
                                  data-testid={`pdf-btn-${report.id}`}
                                  title="Gerar PDF desta medição"
                                >
                                  <FileText className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadSingleDocx(report)}
                                  data-testid={`docx-btn-${report.id}`}
                                  title="Baixar Word (.docx) desta medição"
                                >
                                  <FileDown className="w-4 h-4 text-sky-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(report)}
                                  data-testid={`edit-btn-${report.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-blue-600" />
                                </Button>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(report.id)}
                                    data-testid={`delete-btn-${report.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-sm text-gray-600">
                      Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, reports.length)} de {reports.length}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-3 py-1 text-sm">
                        Página {currentPage} de {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-0 shadow-md bg-blue-50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-gray-800 mb-2">Sobre os Campos</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Potência Instalada:</strong> Capacidade instalada da usina (kW) - carregado automaticamente do cadastro</li>
              <li>• <strong>Energia Gerada:</strong> Total de energia produzida pela usina no período (kWh)</li>
              <li>• <strong>Consumo:</strong> Consumo total de energia no período (kWh)</li>
              <li>• <strong>Energia Média:</strong> Média de energia do período (kWh)</li>
              <li>• <strong>Fatura da Distribuidora:</strong> Valor da fatura da concessionária (R$)</li>
              <li>• <strong>Data de Vencimento:</strong> Data de vencimento da fatura</li>
              <li>• <strong>Data de Leitura:</strong> Data da leitura do medidor</li>
              <li>• <strong>Energia Compensada:</strong> Energia injetada na rede e compensada (kWh)</li>
              <li>• <strong>Saldo Anterior:</strong> Saldo acumulado do mês anterior (kWh) - carregado automaticamente</li>
              <li>• <strong>Saldo Acumulado:</strong> Saldo de créditos de energia acumulados (kWh)</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
